import { ListingsStats, ListingsStatsByDate } from "../api/models";
import { useState } from "react";
import SalesRevenueBreakdown from "./SalesRevenueBreakdown";

export interface Props {
    stats: ListingsStatsByDate[];
}

interface VenueBreakdown {
    account: string;
    venue: string;
}

export const SalesRevenueSummary = (props: Props) => {
    const currencyFormat = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    const [venueBreakdown, setVenueBreakdown] = useState<VenueBreakdown>();

    const summarized: { [key: string]: ListingsStats } = props.stats.reduce(
        (acc: { [key: string]: ListingsStats }, val: ListingsStatsByDate) => {
            for (const item of val.stats) {
                let summary: ListingsStats = acc[item.venue + item.account];
                if (!summary) {
                    summary = {
                        account: item.account,
                        venue: item.venue,
                        recurringRevenueUSD: 0,
                        revenueUSD: 0,
                        numSales: 0,
                        numPayouts: 0,
                    };
                }
                summary.revenueUSD += item.revenueUSD;
                summary.recurringRevenueUSD += item.recurringRevenueUSD;
                summary.numSales += item.numSales;
                summary.numPayouts += item.numPayouts;
                acc[item.venue + item.account] = summary;
            }
            return acc;
        },
        {},
    );

    const sortedStats = Object.values(summarized).sort((a, b) => b.revenueUSD - a.revenueUSD);

    return (
        <div className="h-full flex flex-col">
            <table className="w-full" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                    <col style={{ width: '34%' }} />
                    <col style={{ width: '33%' }} />
                    <col style={{ width: '33%' }} />
                </colgroup>
                <thead>
                    <tr>
                        <th className="text-left text-2xs uppercase tracking-wider text-text-3 font-semibold py-1.5 px-1.5">Venue</th>
                        <th className="text-right text-2xs uppercase tracking-wider text-text-3 font-semibold py-1.5 px-1.5">Rev</th>
                        <th className="text-right text-2xs uppercase tracking-wider text-text-3 font-semibold py-1.5 px-1.5">Recur</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedStats.map((s) => (
                        <tr key={s.venue + s.account} className="border-t border-border hover:bg-bg-elevated transition-colors">
                            <td className="py-1 px-1.5">
                                <button
                                    type="button"
                                    onClick={() => setVenueBreakdown({ venue: s.venue, account: s.account })}
                                    className="hover:opacity-80 cursor-pointer truncate max-w-full block text-left leading-tight"
                                >
                                    <span className="text-accent text-sm font-medium">{s.venue || 'Unknown'}</span>
                                    {s.account?.length > 0 && (
                                        <span className="block text-text-3 text-2xs truncate">{s.account}</span>
                                    )}
                                </button>
                            </td>
                            <td className="py-1.5 px-1.5 text-right font-mono text-sm text-text tabular-nums truncate">
                                {currencyFormat.format(s.revenueUSD)}
                            </td>
                            <td className="py-1.5 px-1.5 text-right font-mono text-sm text-text-2 tabular-nums truncate">
                                {currencyFormat.format(s.recurringRevenueUSD)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {venueBreakdown && (
                <SalesRevenueBreakdown
                    onClose={() => setVenueBreakdown(undefined)}
                    data={props.stats.map((a) => ({
                        date: a.date,
                        stats: a.stats.filter(
                            (s) => s.account === venueBreakdown.account && s.venue === venueBreakdown.venue,
                        ),
                    }))}
                    venue={venueBreakdown.venue}
                    account={venueBreakdown.account}
                />
            )}
        </div>
    );
};
